import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useBlogList } from "@/lib/blogApi";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import BlogDiscovery from "@/components/blog/BlogDiscovery";
import ArticleCard from "@/components/blog/ArticleCard";
import Breadcrumbs, { buildBreadcrumbSchema } from "@/components/site/Breadcrumbs";

const PAGE_SIZE = 12;

export default function BlogList() {
  const [params, setParams] = useSearchParams();
  const { data: ss } = useSiteSettings();
  const page = Math.max(1, parseInt(params.get("page") || "1", 10));
  const search = params.get("q") || "";
  const category = params.get("category") || "";
  const tag = params.get("tag") || "";
  const vertical = params.get("vertical") || "";
  const [searchInput, setSearchInput] = useState(search);
  const { loading, error, data } = useBlogList({
    page, limit: PAGE_SIZE, search, category, tag, vertical,
  });
  const docs = data?.docs || [];
  const totalPages = data?.totalPages || 1;

  function updateParam(key, value) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    // reset page when filters change
    if (key !== "page") next.delete("page");
    setParams(next);
  }

  function onSubmitSearch(e) {
    e.preventDefault();
    updateParam("q", searchInput.trim());
  }

  const isFiltered = Boolean(search || category || tag || vertical);
  // Self-canonical for pagination pages; filters are excluded from canonical.
  const canonical = page > 1 ? `${_origin()}/blog?page=${page}` : `${_origin()}/blog`;
  const siteName = ss?.displayName || ss?.companyName || "MTS";
  const pageSuffix = page > 1 ? ` — Page ${page}` : "";
  const title = `Blog — ${siteName}${pageSuffix}`;
  const description = `Furniture sourcing insights from ${ss?.displayName || "MTS"} — factory audits, supplier verification, and Asia trade guides.${page > 1 ? ` Page ${page}.` : ""}`;

  const breadcrumbItems = [{ name: "Home", href: "/" }, { name: "Blog" }];
  const breadcrumbSchema = buildBreadcrumbSchema(breadcrumbItems, _origin());

  return (
    <main className="max-w-6xl mx-auto px-4 py-12">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        {isFiltered && <meta name="robots" content="noindex,follow" />}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        {breadcrumbSchema && (
          <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        )}
      </Helmet>

      <Breadcrumbs items={breadcrumbItems} className="mb-6" />

      <header className="mb-10">
        <h1 className="text-4xl sm:text-5xl font-semibold text-neutral-900 tracking-tight" data-testid="blog-h1">Blog</h1>
        <p className="text-neutral-600 mt-3 text-lg leading-relaxed">
          Sourcing insights, factory audits and Asia trade guides.
        </p>
      </header>

      {/* Unified Blog discovery — Guides + Industries in one premium
          section. Renders only on the unfiltered first page so it stays
          out of paginated / filtered listings (SEO cleanliness). */}
      {page === 1 && !isFiltered && <BlogDiscovery />}

      <form onSubmit={onSubmitSearch} className="mb-8 flex flex-wrap gap-2" data-testid="blog-search-form">
        <input type="search" value={searchInput}
               onChange={(e) => setSearchInput(e.target.value)}
               placeholder="Search articles…"
               className="flex-1 min-w-0 border border-neutral-300 rounded px-3 py-2 focus:outline-none focus:border-neutral-900" />
        <button type="submit" className="bg-neutral-900 text-white rounded px-4 py-2 text-sm font-medium hover:bg-neutral-800">
          Search
        </button>
        {(search || category || tag || vertical) && (
          <button type="button" onClick={() => setParams({})}
                   className="text-sm text-neutral-500 hover:text-neutral-900 px-3">
            Clear
          </button>
        )}
      </form>

      {(category || tag || vertical) && (
        <div className="mb-6 text-sm text-neutral-600">
          Filtering by:{" "}
          {vertical && <span className="bg-neutral-100 rounded px-2 py-0.5 mr-2">Industry: {vertical}</span>}
          {category && <span className="bg-neutral-100 rounded px-2 py-0.5 mr-2">Category: {category}</span>}
          {tag && <span className="bg-neutral-100 rounded px-2 py-0.5">Tag: {tag}</span>}
        </div>
      )}

      {loading && <div className="text-neutral-500 py-8" data-testid="blog-loading">Loading…</div>}
      {error && <div className="text-red-700 py-8">{error}</div>}
      {!loading && !error && docs.length === 0 && (
        <div className="text-neutral-500 py-8" data-testid="blog-empty">
          No articles yet. Come back soon.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
        {docs.map((p) => (
          <ArticleCard key={p.id} article={p} />
        ))}
      </div>

      {totalPages > 1 && (
        <Pagination page={page} totalPages={totalPages} params={params} />
      )}
    </main>
  );
}

function Pagination({ page, totalPages, params }) {
  return (
    <nav className="mt-10 flex flex-wrap items-center gap-2" aria-label="Pagination" data-testid="blog-pagination">
      {page > 1 && (
        <Link to={_pageHref(params, page - 1)} rel="prev" data-testid="pagination-prev"
              className="text-sm rounded px-3 py-1 border border-neutral-300 text-neutral-800 hover:border-neutral-900">
          ← Prev
        </Link>
      )}
      {_pageItems(page, totalPages).map((item, i) =>
        item === "…" ? (
          <span key={`e${i}`} className="text-sm text-neutral-400 px-1" aria-hidden="true">…</span>
        ) : item === page ? (
          <span key={item} aria-current="page" data-testid={`pagination-page-${item}`}
                className="text-sm rounded px-3 py-1 border bg-neutral-900 text-white border-neutral-900">
            {item}
          </span>
        ) : (
          <Link key={item} to={_pageHref(params, item)} data-testid={`pagination-page-${item}`}
                className="text-sm rounded px-3 py-1 border border-neutral-300 text-neutral-800 hover:border-neutral-900">
            {item}
          </Link>
        )
      )}
      {page < totalPages && (
        <Link to={_pageHref(params, page + 1)} rel="next" data-testid="pagination-next"
              className="text-sm rounded px-3 py-1 border border-neutral-300 text-neutral-800 hover:border-neutral-900">
          Next →
        </Link>
      )}
    </nav>
  );
}

// Compact page list: 1 … 4 5 6 … 12
function _pageItems(page, totalPages) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const items = [1];
  if (page > 3) items.push("…");
  for (let n = Math.max(2, page - 1); n <= Math.min(totalPages - 1, page + 1); n++) items.push(n);
  if (page < totalPages - 2) items.push("…");
  items.push(totalPages);
  return items;
}

function _pageHref(params, n) {
  const next = new URLSearchParams(params);
  if (n > 1) next.set("page", String(n)); else next.delete("page");
  const s = next.toString();
  return s ? `/blog?${s}` : "/blog";
}

function _origin() {
  try { return window.location.origin; } catch { return ""; }
}
